import { useEffect, useRef, type CSSProperties, type ReactNode } from "react"
import type { LoopState, PermanentState } from "../types"
import { getLocationById } from "../game/content"
import { formatCalendarDate, formatDay } from "../game/calendarEngine"
import { commandPointsUsed, getStacksAtLocation } from "../game/armyEngine"
import { getRitualDeadlineDay } from "../game/loopEngine"
import { getKnownSchedule, getNextKnownEvent } from "../game/scheduleEngine"
import { getSpiritAudiencesRemaining, getSpiritQuestions } from "../game/spiritEngine"
import { getAvatarStyle } from "../game/sprites"
import { AmbientMotes } from "./AmbientMotes"
import { CelestialDial } from "./CelestialDial"
import { NAV_ITEMS, type NavTarget } from "./nav"

interface AppShellProps {
  permanent: PermanentState
  loop: LoopState
  navDisabled: boolean
  /** Which nav entry is currently open, so the player can see where they are. */
  activeNav: NavTarget | null
  /** The coronation is showing: the celestial dial holds the sun at full noon. */
  celestialRestored: boolean
  saveStatus: string | null
  /** The Settings screen is open, so its button reads as the current one. */
  settingsActive: boolean
  /**
   * Which screen is currently in the content area. Used only to move keyboard
   * focus into the scroll region when it changes (see below) — the shell locks
   * the window, so without this the arrow/Page keys have nothing to scroll until
   * the player happens to click something inside the content.
   */
  screenKey: string
  /**
   * Overrides the content area's `--screen-backdrop` for screens whose
   * illustration is chosen at runtime rather than fixed per screen key — the
   * battle screen, whose backdrop is the specific battle's scene. Left undefined
   * on every other screen, which keeps the per-screen-key backdrop from the CSS.
   */
  contentBackdrop?: string
  onNavigate: (target: NavTarget) => void
  onSave: () => void
  onOpenSettings: () => void
  onMainMenu: () => void
  children: ReactNode
}

/**
 * Things waiting on the player behind a screen they are not currently looking
 * at. A time-loop game punishes forgetting, and all three of these were
 * previously invisible until the screen was opened on the off chance: an event
 * firing today, spirit audiences that expire when the loop turns over, and
 * soldiers standing in this very location waiting to be picked up.
 */
function getNavBadges(
  permanent: PermanentState,
  loop: LoopState,
): Partial<Record<NavTarget, { count: number; title: string; urgent: boolean }>> {
  const badges: Partial<Record<NavTarget, { count: number; title: string; urgent: boolean }>> = {}

  const today = getKnownSchedule(permanent, loop).filter(
    (entry) => entry.status === "today",
  ).length
  if (today > 0) {
    badges.schedule = {
      count: today,
      title: `${today} thing${today === 1 ? "" : "s"} you know of happens today`,
      urgent: true,
    }
  }

  const audiences = getSpiritAudiencesRemaining(loop)
  const hasQuestionToAsk = getSpiritQuestions(permanent).some(
    (entry) => entry.unlocked && !entry.asked,
  )
  if (audiences > 0 && hasQuestionToAsk) {
    badges.spirit = {
      count: audiences,
      title: `${audiences} question${audiences === 1 ? "" : "s"} the spirit will still take this loop`,
      urgent: false,
    }
  }

  const waiting = getStacksAtLocation(loop, loop.currentLocationId)
  const freeCommandPoints = loop.commandPoints - commandPointsUsed(loop.currentArmy)
  if (waiting.length > 0 && freeCommandPoints > 0) {
    badges.army = {
      count: waiting.length,
      title: `${waiting.length} contingent${waiting.length === 1 ? "" : "s"} waiting here, and command to spare`,
      urgent: false,
    }
  }

  return badges
}

export function AppShell({
  permanent,
  loop,
  navDisabled,
  activeNav,
  celestialRestored,
  saveStatus,
  settingsActive,
  onNavigate,
  onSave,
  onOpenSettings,
  onMainMenu,
  screenKey,
  contentBackdrop,
  children,
}: AppShellProps) {
  const locationName = getLocationById(loop.currentLocationId)?.name ?? loop.currentLocationId
  const nextEvent = getNextKnownEvent(permanent, loop)
  const badges = getNavBadges(permanent, loop)

  // Only .app-content scrolls (the window is locked), and a scroll region has to
  // hold focus for the keyboard to drive it. On every screen change we move
  // focus into the content so the arrow, Page and Space keys scroll it straight
  // away — instead of doing nothing until the player first clicks a control
  // inside it. We also snap the scroll back to the top: the same element is
  // reused across screens, so without this a new screen would open scrolled to
  // wherever the last one was left. The Event screen is left alone: it keeps its
  // own "advance" button focused so the conversation can be paged from the
  // keyboard.
  const contentRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (screenKey === "event") return
    const content = contentRef.current
    if (!content) return
    content.scrollTop = 0
    content.focus({ preventScroll: true })
  }, [screenKey])

  return (
    <div className="app-shell">
      {/* The air of the world, drifting over the whole scene and turning from
          gold dust to falling ash as the loop runs down toward night — driven
          by the same data-timeofday the ambient light and the dial read. */}
      <AmbientMotes />
      <header className="app-header">
        <h1>Echoes Before Eternity</h1>
      </header>

      <nav className="app-nav">
        {NAV_ITEMS.map(({ target, label }, index) => {
          const badge = badges[target]
          return (
            <button
              key={target}
              type="button"
              disabled={navDisabled}
              className={activeNav === target ? "app-nav-active" : ""}
              aria-current={activeNav === target ? "page" : undefined}
              // The number key that opens this screen (see App's key handler).
              title={badge ? badge.title : `${label} — press ${index + 1}`}
              onClick={() => onNavigate(target)}
            >
              <span className="app-nav-key" aria-hidden="true">
                {index + 1}
              </span>
              <span className="app-nav-label">{label}</span>
              {badge && (
                <span className={`app-nav-badge${badge.urgent ? " urgent" : ""}`}>
                  {badge.count}
                </span>
              )}
            </button>
          )
        })}
        <button type="button" disabled={navDisabled} onClick={onSave}>
          Save Game
        </button>
        {saveStatus && <p className="app-nav-save-status">{saveStatus}</p>}
        {/* Settings returns to the map when closed, so it is held back during a
            battle (whose turn state would be lost by leaving) exactly like the
            screen nav — while Main Menu, which deliberately leaves the world,
            stays reachable throughout. */}
        <button
          type="button"
          disabled={navDisabled}
          className={settingsActive ? "app-nav-active" : ""}
          aria-current={settingsActive ? "page" : undefined}
          onClick={onOpenSettings}
        >
          Settings
        </button>
        <button type="button" className="app-nav-main-menu" onClick={onMainMenu}>
          Main Menu
        </button>
      </nav>

      {/* tabIndex -1 makes this a programmatic focus target (not a Tab stop) so
          it can hold focus and take keyboard scrolling; see the effect above. */}
      {/* data-screen lets the stylesheet lay a full-bleed illustration across the
          whole reading column on the terminal beats (death, the coronation)
          without any screen needing to reach outside its own box. */}
      <main
        className="app-content"
        ref={contentRef}
        tabIndex={-1}
        data-screen={screenKey}
        // An inline custom property beats the stylesheet's per-screen value, so
        // a runtime-chosen backdrop (the current battle's scene) flows straight
        // into the existing background layers without a per-battle CSS rule.
        style={
          contentBackdrop
            ? ({ "--screen-backdrop": contentBackdrop } as CSSProperties)
            : undefined
        }
      >
        {children}
      </main>

      <aside className="app-panel">
        {/* The face the player chose at the start, carried through every loop. */}
        <div
          className="app-panel-avatar"
          style={getAvatarStyle(permanent.avatarIndex)}
          role="img"
          aria-label="Your chosen face"
        />
        <CelestialDial currentDay={loop.currentDay} restored={celestialRestored} />
        <dl className="status-line">
          <dt>Loop</dt>
          <dd>{loop.loopNumber}</dd>
          {/* The day counter is the unit every piece of foreknowledge in the
              game is expressed in — journal entries, event schedules and the
              Day 512 deadline all speak in day numbers. The fantasy calendar
              below it is flavour, and cannot substitute for this. */}
          <dt>Day</dt>
          <dd>
            {loop.currentDay}
            <span className="status-subline">{formatCalendarDate(loop.currentDay)}</span>
          </dd>
          <dt>Location</dt>
          <dd>{locationName}</dd>
          {loop.money > 0 && (
            <>
              <dt>Coin</dt>
              <dd>{loop.money}</dd>
            </>
          )}
          {permanent.totalDeaths > 0 && (
            <>
              <dt>Deaths</dt>
              <dd>{permanent.totalDeaths}</dd>
            </>
          )}
          {/* Command Points come from Leadership *and* from faction trust, so
              gate the readout on the total rather than on the ability alone —
              otherwise a player who earned them purely through diplomacy can
              lead units without ever seeing their capacity. */}
          {loop.commandPoints > 0 && (
            <>
              <dt>Command Points</dt>
              <dd>
                {commandPointsUsed(loop.currentArmy)} / {loop.commandPoints}
              </dd>
            </>
          )}
          {/* A standing reminder of the next thing the player knows is coming,
              so the schedule does not have to be opened to be acted on. */}
          {nextEvent && (
            <>
              <dt>Next Known Event</dt>
              <dd>
                {formatDay(nextEvent.event.day)}
                <span className="status-subline">
                  {nextEvent.locationName}
                  {nextEvent.daysUntil > 0 &&
                    ` — in ${nextEvent.daysUntil} day${nextEvent.daysUntil === 1 ? "" : "s"}`}
                  {nextEvent.daysUntil === 0 && " — today"}
                </span>
              </dd>
            </>
          )}
          {permanent.deadlineRevealed && (
            <>
              <dt>Days Remaining</dt>
              <dd>
                {Math.max(0, getRitualDeadlineDay(loop.loopNumber) - loop.currentDay)}
                <span className="status-subline">
                  Deadline: {formatDay(getRitualDeadlineDay(loop.loopNumber))}
                </span>
              </dd>
              <dt>Objective</dt>
              <dd className="status-objective">
                Stop the Necromancer before the sun is extinguished.
              </dd>
            </>
          )}
        </dl>
      </aside>
    </div>
  )
}
